import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Link, withRouter } from "react-router-dom";
import _ from "lodash";
import moment from "moment/moment";
import styled from "styled-components";
import { Translate, I18n } from "react-redux-i18n";
import Constants from "../../AppConstants/Constants";
import Utilities from "../../Helpers/Utilities";
import Layout from "../../Layouts/LayoutMain";
import CartPanel from "../../Components/Cart/CartPanel";
import PaymentOptionsContainer from "../../Components/Cart/PaymentOptionsContainer";
import { PageTitle } from "../../Components/Common/PageTitle";
import { ContentContainer } from "../../Components/Common/ContentContainer";
import AddAddressModal from "../../Components/Modals/AddressModal";
import LeaseModal from "../../Components/Modals/LeaseModal";
import { getXsrfToken } from "../../Store/Security/Actions";
import { logout } from "../../Store/Account/Actions";
import { clearCart } from "../../Store/Cart/Actions";
import { updateCreateAccountModalState, updateLeaseModalState, updateAddAddressModalState } from "../../Store/Modals/Actions";
import { toggleBlockingStatus } from "../../Store/Status/Actions";

// region Styles
const Alert = styled.div`
    margin-bottom: 20px;
    padding: 30px;
`;

const CustomP = styled.p`
    margin-bottom: 0;
    
    ${Alert} & {
        color: #666;
    }
`;

const StyledLink = styled(Link)`
    color: #666 !important;
    font-weight: 600;
    text-decoration: underline;
`;

const StyledTranslate = styled(Translate)`
    color: #666 !important;
`;

const CustomS = styled.span`
    color: white !important;
`;
// endregion

class Cart extends Component {
    constructor(props) {
        super(props);

        this.clearCartSummary = this.clearCartSummary.bind(this);
        this.openModal = this.openModal.bind(this);
        this.openAddressModal = this.openAddressModal.bind(this);
        this.printPdf = this.printPdf.bind(this);
        this.showLeaseModal = this.showLeaseModal.bind(this);
    }

    clearCartSummary() {
        const { clearCart, getXsrfToken } = this.props;

        getXsrfToken(({ token }) => {
            clearCart({ __RequestVerificationToken: token });
        });
    }

    openModal() {
        this.props.updateCreateAccountModalState(true);
    }

    openAddressModal() {
        this.props.updateAddAddressModalState(true);
    }

    showLeaseModal() {
        const { updateLeaseModalState } = this.props;

        updateLeaseModalState(true);
    }

    printPdf() {
        const { toggleBlockingStatus, userInfo, logout } = this.props;

        toggleBlockingStatus(true);

        // if user does NOT have and account make them create one before printing the cart so pdf can have header with user info
        // if they have an account but no address on file make them input an address
        if (userInfo.isGuest) {
            this.openModal();
            toggleBlockingStatus(false);
        } else if (!userInfo.isGuest && userInfo.addresses.length < 1) {
            this.openAddressModal();
            toggleBlockingStatus(false);
        } else {
            fetch(Utilities.buildUrl("/Printing/GeneratePdfReport?name=PrintCart"), {
                ...Constants.FETCH_REQUEST_CONFIG,
                method: "GET",
                headers: {
                    "Accept": "application/pdf"
                }
            })
                .then(response => {
                    if (!response.ok) {
                        throw response;
                    }

                    return response.blob();
                })
                .then(data => {
                    Utilities.showFile(data, "VelocitorStoreCart");
                    toggleBlockingStatus(false);
                })
                .catch(error => {
                    if (error.status === 401 || error.status === 403) {
                        logout();
                    } else if (error.redirect) {
                        window.location.href = error.url;
                    } else {
                        const errorText = error.clone();
                        if (typeof error.json === "function") {
                            error.json()
                                .then(json => {
                                    toggleBlockingStatus(false);
                                    alert(json.value);
                                })
                                .catch(err => {
                                    if (typeof errorText.text === "function") {
                                        errorText.text()
                                            .then(text => {
                                                toggleBlockingStatus(false);
                                                alert(text);
                                            })
                                            .catch(err => {
                                                toggleBlockingStatus(false);
                                                alert(I18n.t("alerts.genericError"));
                                            });
                                    }
                                });
                        }
                    }
                });
        }
    }

    render() {
        const { isAuthenticated, cart, userInfo, config: { LEASE_RATE } } = this.props;
        const items = {
            kits: [],
            services: [],
            accessories: [],
            vtrack: []
        };

        if (!cart.items || cart.items.length === 0) {
            return (
                <Layout isAuthenticated={isAuthenticated}>
                    <section className="text-center ">
                        <div className="container">
                            <div className="row">
                                <div className="col-md-8 col-lg-6">
                                    <div className="cta">
                                        <h2><Translate value="cart.cartEmpty" /></h2>
                                        <Link className="btn btn--primary type--uppercase" to="/">
                                            <CustomS>
                                                <Translate value="cart.continueShopping" />
                                            </CustomS>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </Layout>
            );
        } else {
            const eligibleToBuy = userInfo.purchaseEligibilityDate ? moment(userInfo.purchaseEligibilityDate).diff(moment(), "days") < 1 : false;
            const isCustomMessage = moment(userInfo.purchaseEligibilityDate).format("YYYY-MM-DD") === "2020-06-30";
            const purchaseEligibilityDate = isCustomMessage ? "TBD" : I18n.l(userInfo.purchaseEligibilityDate, { dateFormat: "locale.date.short" });
            const onTheRoadEligibilityDate = isCustomMessage ? "TBD" : I18n.l(userInfo.onTheRoadEligibilityDate, { dateFormat: "locale.date.short" });
            const alertClassName = !eligibleToBuy ? "bg--error" : "bg--primary";

            let totalAmount = 0;
            let monthlyAmount = 0;

            _.each(cart.items, item => {
                if (item.productDescription) {
                    item.productDescription = item.productDescription.replace(/(<([^>]+)>)/ig, "");
                }

                if (item.isRecurring) {
                    items.services.push(item);
                    monthlyAmount += item.subTotalValue || 0;
                } else if (item.categorySeName.toLowerCase().includes("accessories")) {
                    items.accessories.push(item);
                    totalAmount += item.subTotalValue || 0;
                } else if (item.categorySeName.toLowerCase().includes("vtrack") || item.productName.toLowerCase().includes("vedr")) {
                    items.vtrack.push(item);
                    totalAmount += item.subTotalValue || 0;
                } else {
                    items.kits.push(item);
                    totalAmount += item.subTotalValue || 0;
                }
            });

            const leaseAmount = totalAmount * LEASE_RATE;

            return (
                <Layout isAuthenticated={isAuthenticated}>
                    <PageTitle title="cart.title"/>
                    <ContentContainer containerClassName="summary-container">
                        <div className="row justify-content-end" style={{ marginBottom: 20 }}>
                            <div className="col-md-3 col-lg-2">
                                <button type="submit" className="btn print-cart-btn" onClick={this.printPdf}><Translate value="cart.printCart" /></button>
                            </div>

                            <div className="col-md-3 col-lg-2">
                                <button type="submit" className="btn clear-cart-btn" onClick={this.clearCartSummary}><Translate value="cart.clearCart" /></button>
                            </div>
                        </div>

                        <div className="row summary-panel-row">
                            {items.kits.length > 0 &&
                            <div className="col-12">
                                <CartPanel
                                    title={I18n.t("cart.panels.kitsDevices")}
                                    items={items.kits}
                                    showDescription={true}
                                />
                            </div>}

                            {items.vtrack.length > 0 &&
                            <div className="col-12">
                                <CartPanel
                                    title="VTrack"
                                    items={items.vtrack}
                                    showDescription={true}
                                />
                            </div>}

                            {items.accessories.length > 0 &&
                            <div className="col-12">
                                <CartPanel
                                    title={I18n.t("cart.panels.accessories")}
                                    items={items.accessories}
                                    showDescription={true}
                                />
                            </div>}

                            {items.services.length > 0 &&
                            <div className="col-12">
                                <CartPanel
                                    title={I18n.t("cart.panels.services")}
                                    items={items.services}
                                    showDescription={true}
                                />
                            </div>}
                        </div>

                        <div className="row justify-content-end">
                            <div className="col-md-6 col-lg-4">
                                <div className="boxed boxed--border text-right text-left-sm">
                                    <div>
                                        <strong style={{ color: "#252525", fontSize: "1.15em" }}><Translate value="cart.totalDue" />: {Utilities.formatPrice(totalAmount)}</strong>
                                    </div>
                                    <small><Translate value="cart.shippingTaxes" /></small>
                                </div>
                            </div>
                        </div>

                        <div className="row justify-content-center">
                            <div className="col-12">
                                <div className="row">
                                    <div className="col-12">
                                        <h2 className="text-center" style={{ paddingTop: 20 }}><Translate value="cart.payment.title" /></h2>

                                        <Alert className={`alert ${alertClassName}`}>
                                            <div className="alert__body">
                                                <CustomP>
                                                    <strong><StyledTranslate value="cart.payment.customerAlert.station" />:</strong> {userInfo.station}
                                                </CustomP>
                                                <CustomP>
                                                    <strong><StyledTranslate value="cart.payment.customerAlert.purchaseEligibilityDate" />:</strong> {purchaseEligibilityDate}
                                                </CustomP>
                                                <CustomP>
                                                    <strong><StyledTranslate value="cart.payment.customerAlert.onTheRoadEligibilityDate" />:</strong> {onTheRoadEligibilityDate}
                                                </CustomP>

                                                {isCustomMessage &&
                                                <CustomP>
                                                    <StyledTranslate value="cart.payment.customerAlert.customMessage" /> {moment(userInfo.purchaseEligibilityDate).format("YYYY")}.
                                                </CustomP>}

                                                {userInfo && !eligibleToBuy && userInfo.isGuest &&
                                                <CustomP>
                                                    <StyledTranslate value="cart.payment.customerAlert.guestMessage" /> <StyledLink to="#" onClick={this.openModal}><StyledTranslate value="cart.payment.customerAlert.createAccount" /></StyledLink>.
                                                </CustomP>}

                                                {userInfo && !eligibleToBuy && !userInfo.isGuest &&
                                                <CustomP>
                                                    <StyledTranslate value="cart.payment.customerAlert.saveItemsToCart" />
                                                </CustomP>}

                                                <CustomP>
                                                    <StyledTranslate value="cart.payment.customerAlert.needAssistance1" /> <StyledLink to={Constants.ENDPOINTS.SUPPORT}><StyledTranslate value="cart.payment.customerAlert.needAssistance2" /></StyledLink> <StyledTranslate value="cart.payment.customerAlert.needAssistance3" />
                                                </CustomP>
                                                <CustomP>
                                                    <StyledTranslate value="cart.payment.customerAlert.thankYou" />
                                                </CustomP>
                                            </div>
                                        </Alert>

                                        <PaymentOptionsContainer
                                            options={[
                                                {
                                                    name: I18n.t("cart.payment.options.payInFull.title"),
                                                    items: [
                                                        {
                                                            description: I18n.t("cart.payment.options.payInFull.desc1"),
                                                            size: 8,
                                                            amount: totalAmount
                                                        },
                                                        {
                                                            description: I18n.t("cart.payment.options.payInFull.desc2"),
                                                            size: 8,
                                                            amount: monthlyAmount
                                                        }
                                                    ],
                                                    note: I18n.t("cart.payment.options.payInFull.note"),
                                                    disabled: !eligibleToBuy
                                                },
                                                {
                                                    name: I18n.t("cart.payment.options.lease.title"),
                                                    items: [
                                                        {
                                                            description: I18n.t("cart.payment.options.lease.desc1"),
                                                            size: 9,
                                                            amount: leaseAmount
                                                        },
                                                        {
                                                            description: I18n.t("cart.payment.options.lease.desc2"),
                                                            size: 9,
                                                            amount: monthlyAmount
                                                        }
                                                    ],
                                                    note: I18n.t("cart.payment.options.lease.note"),
                                                    disabled: !eligibleToBuy,
                                                    onClick: this.showLeaseModal,
                                                    cartItems: cart.items
                                                }
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ContentContainer>
                    <AddAddressModal/>
                    <LeaseModal/>
                </Layout>
            );
        }
    }
}

function mapStateToProps(state) {
    return {
        i18n: state.i18n,
        config: state.config,
        userInfo: state.userInfo,
        cart: state.cart,
        numOfSteps: state.wizardNumOfSteps,
        currentStep: state.wizardCurrentStep
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getXsrfToken: getXsrfToken,
        logout: logout,
        clearCart: clearCart,
        updateCreateAccountModalState: updateCreateAccountModalState,
        updateAddAddressModalState: updateAddAddressModalState,
        updateLeaseModalState: updateLeaseModalState,
        toggleBlockingStatus: toggleBlockingStatus
    }, dispatch);
}


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Cart));
